import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { nextKenBurnsClass } from './entrances.js';

const TRANSITION_STYLES = ['fade', 'zoom', 'left', 'right', 'scale'];
const TRANSITION_MS = 750;

function SlideContent({ item, kbClass, slideDurationMs, settings }) {
    return (
        <>
            {item.file_type === 'video' ? (
                <video
                    className="absolute inset-0 w-full h-full object-contain"
                    src={item.mp4_url || item.url}
                    poster={item.preview_url || undefined}
                    autoPlay muted playsInline
                />
            ) : (
                <img
                    className={`absolute inset-0 w-full h-full object-contain ${kbClass ?? ''}`}
                    style={{ '--lw-slide-duration': `${slideDurationMs}ms` }}
                    src={item.url}
                    alt=""
                />
            )}

            {/* Caption + name */}
            {((settings.showCaptions !== false && item.caption) || (settings.showNames !== false && item.uploader_name)) && (
                <div className="absolute bottom-20 inset-x-0 flex flex-col items-center gap-2 pointer-events-none">
                    {settings.showCaptions !== false && item.caption && (
                        <span
                            className="bg-black/80 text-white text-xl px-6 py-2 rounded-2xl max-w-2xl text-center leading-snug"
                            style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', textShadow: '0 1px 3px rgba(0,0,0,0.8)' }}
                        >
                            "{item.caption}"
                        </span>
                    )}
                    {settings.showNames !== false && item.uploader_name && (
                        <span className="bg-black/70 text-white text-base font-semibold px-5 py-1.5 rounded-2xl" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}>
                            📸 {item.uploader_name}
                        </span>
                    )}
                </div>
            )}
        </>
    );
}

export default function CinematicSlide({ items, newItemCount = 0, slideDurationSec = 10, settings = {} }) {
    const { t } = useTranslation();
    const SLIDE_DURATION_MS = slideDurationSec * 1000;

    const [currentIdx, setCurrentIdx] = useState(0);
    const [prevIdx, setPrevIdx] = useState(null);
    const [slideKey, setSlideKey] = useState(0);
    const [transStyleIdx, setTransStyleIdx] = useState(0);
    const [kbClass, setKbClass] = useState(() => nextKenBurnsClass());
    const timerRef = useRef(null);
    const advanceCounter = useRef(0);
    const seenCountRef = useRef(newItemCount);
    // Track items length to compensate currentIdx when items are prepended
    const prevItemsLengthRef = useRef(items.length);

    // Shared transition logic used by both advance() and jumpTo()
    const doTransition = useCallback((getNextIdx) => {
        advanceCounter.current += 1;
        const counter = advanceCounter.current;
        setCurrentIdx(prev => {
            const nextIdx = getNextIdx(prev);
            setPrevIdx(prev);
            setSlideKey(k => k + 1);
            setTransStyleIdx(t => t + 1);
            setKbClass(nextKenBurnsClass());
            setTimeout(() => {
                if (advanceCounter.current === counter) setPrevIdx(null);
            }, TRANSITION_MS + 100);
            return nextIdx;
        });
    }, []);

    const advance = useCallback(() => {
        doTransition(prev => (prev + 1) % Math.max(items.length, 1));
    }, [items.length, doTransition]);

    // When items are prepended (new arrivals), currentIdx would silently point to a
    // different photo. Compensate by shifting forward so the displayed photo stays the same.
    useEffect(() => {
        const delta = items.length - prevItemsLengthRef.current;
        prevItemsLengthRef.current = items.length;
        if (delta > 0) {
            setCurrentIdx(prev => Math.min(prev + delta, items.length - 1));
        }
    }, [items.length]);

    // When a new item arrives, jump directly to it (index 0) with a transition animation.
    // This fires after the length-compensation effect, so prevIdx correctly shows the
    // item that was on screen before the arrival.
    useEffect(() => {
        if (newItemCount <= seenCountRef.current) return;
        seenCountRef.current = newItemCount;
        doTransition(() => 0);
    }, [newItemCount, doTransition]);

    // Auto-advance timer
    useEffect(() => {
        if (!items.length) return;
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(advance, SLIDE_DURATION_MS);
        return () => clearTimeout(timerRef.current);
    }, [currentIdx, items.length, SLIDE_DURATION_MS, advance]);

    if (!items.length) {
        return (
            <div className="flex items-center justify-center h-full bg-black">
                <p className="text-white/50 text-2xl">{t('Waiting for the first photo…')}</p>
            </div>
        );
    }

    const safeIdx = n => Math.min(n, items.length - 1);
    const currentItem = items[safeIdx(currentIdx)];
    const prevItem = prevIdx !== null ? items[safeIdx(prevIdx)] : null;
    const trans = TRANSITION_STYLES[transStyleIdx % TRANSITION_STYLES.length];

    return (
        <div className="relative w-full h-full overflow-hidden bg-black">
            {/* Outgoing slide */}
            {prevItem && (
                <div key={`out-${slideKey}`} className={`absolute inset-0 lw-cin-exit-${trans}`}>
                    <SlideContent item={prevItem} kbClass={null} slideDurationMs={SLIDE_DURATION_MS} settings={settings} />
                </div>
            )}

            {/* Incoming slide */}
            <div key={`in-${slideKey}`} className={`absolute inset-0 lw-cin-enter-${trans}`}>
                <SlideContent item={currentItem} kbClass={kbClass} slideDurationMs={SLIDE_DURATION_MS} settings={settings} />
            </div>

            {/* Slide counter */}
            <div className="absolute top-4 right-4 text-white/30 text-sm tabular-nums pointer-events-none" style={{ zIndex: 5 }}>
                {currentIdx + 1} / {items.length}
            </div>
        </div>
    );
}
